import { useState, useEffect, useRef } from 'react';
import { useIsPhone } from '../hooks/useIsPhone';
import {
  getPatientsPage, getPatient, updatePatient,
  getPatientDocuments, uploadPatientDocument, deletePatientDocument, releasePatientDocument,
  getVisitsForPatient,
  getPatientTimeline, generatePatientTimeline, getActiveAdmissionForPatient,
  getPatientBills, markBillPaid, openBillPdf,
} from '../api';
import { loadConfig, getActiveModel } from '../aiConfig';
import MergePatientsModal from './MergePatientsModal';
import MobileUploadQRModal from './MobileUploadQRModal';
import DocumentPreviewButtons from './DocumentPreviewButtons';
import { PrintBillButton } from '../features/printing/PrintBillButton';
import { AISummaryPanel } from '../features/patients/AISummaryPanel';
import { DeceasedStatusDialog } from '../features/patients/DeceasedStatusDialog';
import { MedicalHistoryTabs } from '../features/patients/MedicalHistoryTabs';
import { useAllergies } from '../features/consultation/useAllergies';
import { useConsentRequirements, useRecordConsent } from '../features/consent/useConsent';
import { ConsentStatusBadge } from '../features/consent/ConsentCapturePanel';
import type { ConsentTypeId } from '../features/consent/types';
import { checkBp, checkPulse, checkTemp, checkSpo2, checkBmi } from '../lib/vitalThresholds';
import PatientPrescriptionHistory from '../features/prescriptions/PatientPrescriptionHistory';
import PatientLabHistory from '../features/laboratory/PatientLabHistory';
import { useAuth } from '../hooks/useAuth';
import { Badge } from './ui/Badge';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from './ui/DropdownMenu';
import { Menu } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/Dialog';
import { PriorityTabNav } from './ui/PriorityTabNav';
import { TextPromptDialog } from './ui/TextPromptDialog';
import { AmendNoteDialog, NoteVersionsDialog, VersionTag, canAmendNote, type AmendableNote } from '../features/consultation/NoteAmendment';
import PatientHistoryPanel from './PatientHistoryPanel';
import { patientRecordTabs, type PatientRecordTabId } from '../features/patients/patientRecordTabs';
import { VitalsTrendChart } from './vitals/VitalsTrendChart';
import { ADULT_MIN_AGE, isAdult, normalizeTemp, opdReferenceLines } from '../lib/vitalReferenceBands';
import { PatientAuditTab } from '../features/patients/PatientAuditTab';
import { PatientAdmissionsTab } from '../features/ip-admissions/PatientAdmissionsTab';
import type { AdmissionListRow } from '../features/ip-admissions/api';
import { createErasureRequest } from '../features/admin/data-privacy/api';
import { toast } from '../hooks/useToast';
import { capitalizeNameParts, nameInputProps, patientDisplayName } from '../lib/patientName';
import { useTitleField } from '../features/registration/useIntakeConfig';

const CICONS: Record<string, string> = { 'Lab Report':'🧪','X-Ray / Scan':'🩻','Prescription':'💊','ECG / Echo':'❤️','Discharge Summary':'📋','Consultation Notes':'📝','Other':'📄' };
const CCOL: Record<string, string>   = { 'Lab Report':'#DBEAFE','X-Ray / Scan':'#F3E8FF','Prescription':'#D1FAE5','ECG / Echo':'#FEE2E2','Discharge Summary':'#FEF3C7','Consultation Notes':'#E0E7FF','Other':'#F3F4F6' };
const UPLOAD_CATS = ['Lab Report','X-Ray / Scan','Prescription','ECG / Echo','Discharge Summary','Other'];

interface TimelineEvent {
  category: string;
  severity: 'critical' | 'watch' | 'routine';
  title: string;
  date?: string | null;
  summary?: string;
  source?: string;
}
interface TimelineData {
  events?: TimelineEvent[];
  generated_at?: string;
  stale?: boolean;
  source_count?: number;
}

const TL_CATEGORY: Record<string, { icon: string; label: string }> = {
  diagnosis:   { icon:'🩺', label:'Diagnosis' },
  admission:   { icon:'🏥', label:'Admission' },
  procedure:   { icon:'🔬', label:'Procedure' },
  medication:  { icon:'💊', label:'Medication' },
  allergy:     { icon:'⚠️', label:'Allergy' },
  vital_alert: { icon:'📉', label:'Vital alert' },
  lab_result:  { icon:'🧪', label:'Lab result' },
  visit:       { icon:'🔄', label:'Visit' },
  document:    { icon:'📄', label:'Document' },
  other:       { icon:'📌', label:'Other' },
};
const TL_SEVERITY: Record<string, { label: string; bg: string; border: string; dot: string; text: string }> = {
  critical: { label:'Critical', bg:'#FEE2E2', border:'#DC2626', dot:'#DC2626', text:'#991B1B' },
  watch:    { label:'Watch',    bg:'#FEF3C7', border:'#D97706', dot:'#D97706', text:'#92400E' },
  routine:  { label:'Routine',  bg:'#F0FDF4', border:'#A7F3D0', dot:'#059669', text:'#065F46' },
};

// The API round-trips `dob` (a DATE column) as a UTC-midnight ISO timestamp,
// so slicing that string directly grabs the UTC calendar date — off by a day
// from the intended local date whenever the browser isn't in the same
// timezone the value was stored in. Local getters correctly recover the
// calendar date the server meant.
function toDateInputValue(dob?: string | null) {
  if (!dob) return '';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const STAGE_META: Record<string, { icon: string; label: string }> = {
  verify:       { icon:'🔍', label:'Verify' },
  register:     { icon:'📝', label:'Register' },
  vitals:       { icon:'❤️', label:'Vitals' },
  pre_consult:  { icon:'🩺', label:'Pre-Consult' },
  queue:        { icon:'🎫', label:'Queue' },
  consultation: { icon:'👨‍⚕️', label:'Consultation' },
  ip:           { icon:'🛏', label:'IP' },
  resolve:      { icon:'✅', label:'Resolve' },
};

interface PatientVitals {
  bp_systolic?: number;
  bp_diastolic?: number;
  pulse_rate?: number;
  spo2?: number;
  temperature?: number;
  weight_kg?: number;
  bmi?: number;
  blood_group?: string;
  height_cm?: number;
  symptoms?: string;
  /** Time taken — the clinical measurement time. */
  recorded_at?: string;
  /** Time entered (KMC #36) and the late/amendment markers the trend chart shows. */
  entered_at?: string | null;
  late_entry?: boolean;
  late_entry_reason?: string | null;
  version?: number;
  amendment_reason?: string | null;
  admission_id?: number | null;
}

interface Consultation extends AmendableNote {
  ai_draft?: string | null;
}

export interface Patient {
  id: number;
  uhid: string;
  name: string;
  /** Mr./Mrs./Ms./Miss — its own column, never part of `name`. */
  title?: string | null;
  gender: string;
  age: number | null;
  mobile: string;
  dob?: string | null;
  address?: string | null;
  abha_number?: string | null;
  blood_group?: string | null;
  height_cm?: number | string | null;
  created_at?: string;
  /** Most recent visit's start time, across every branch — only present on
   * the paginated list rows (GET /api/patients' LATERAL join), not on a
   * single-patient fetch. */
  last_visit_at?: string | null;
  vitals?: PatientVitals[];
  documents?: unknown[];
  consultations?: Consultation[];
  /** Set once, by the DPDP erasure-completion action — see
   * backend/routes/data-privacy.js. Non-null means name/mobile/dob/address/
   * abha_number were redacted and the record can no longer be edited. */
  pii_erased_at?: string | null;
  pii_erased_by?: string | null;
  /** Patient-level Deceased status (Communication spec §5.5) — written only
   * via /api/patients/:uhid/deceased, never by the Edit record form. */
  deceased?: boolean;
  deceased_at?: string | null;
  deceased_recorded_by?: string | null;
  deceased_recorded_at?: string | null;
  deceased_reason?: string | null;
}

interface PatientsPage {
  data: Patient[];
  page: number;
  limit: number;
  total: number | null;
  has_more: boolean;
}

const PATIENTS_PAGE_SIZE = 50;

interface ActiveAdmission {
  ward_name: string;
  bed_number: string | number;
  admitted_at: string;
}

interface VisitStage {
  id: number;
  stage: string;
  status: string;
  stage_data?: Record<string, any>;
  completed_at?: string;
  notes?: string;
}

interface Visit {
  id: number;
  visit_id: string;
  started_at: string;
  visit_type: string;
  is_new_patient: boolean;
  current_stage: string;
  resolution?: string;
  resolution_notes?: string;
  stages: VisitStage[];
}

interface Bill {
  id: number;
  bill_number: string;
  total_amount: string | number;
  payment_status: string;
  bill_type: string;
  payment_mode: string;
  created_at: string;
}

interface DocumentItem {
  id: number;
  doc_category: string;
  original_name: string;
  file_type?: string;
  source?: string;
  superseded_by_document_id?: number | null;
  /** Set on a document filed from an approved consultation note. */
  consultation_id?: number | null;
  uploaded_at: string;
  modified_at?: string;
  modified_by?: string;
  /** 'staff' (default) or 'patient' — whether this document is visible on
   *  /patient-api/documents. See migration_patient_document_release.sql. */
  visibility?: 'staff' | 'patient';
  released_at?: string | null;
  /** Set when this file is an outside / reference laboratory report linked to a lab order. */
  lab_context?: {
    reference_lab: string | null;
    report_date: string | null;
    tests: Array<{ order_item_id: number; test_name: string; status: string; order_number: string; report_status: 'current' | 'superseded' }>;
  } | null;
}

/** How an outside-lab report's test reads in the document list. */
function outsideLabStatus(t: { status: string; report_status: string }): { label: string; variant: 'success' | 'warn' | 'neutral' } {
  if (t.report_status === 'superseded') return { label: 'Replaced by a newer report', variant: 'neutral' };
  if (t.status === 'REPORTED') return { label: 'Finalized', variant: 'success' };
  if (t.status === 'VERIFIED') return { label: 'Verified', variant: 'success' };
  if (t.status === 'CANCELLED') return { label: 'Order cancelled', variant: 'neutral' };
  return { label: 'Pending verification', variant: 'warn' };
}

interface PatientRecordsProps {
  preselectUhid?: string;
  onPreselectConsumed?: () => void;
  onOpenVisit?: (visitId: string) => void;
  /** Opens an admission's Case Sheet in the IP module (Admissions tab). */
  onOpenAdmission?: (admission: AdmissionListRow) => void;
  /**
   * True when mounted inside another screen's own viewer (e.g. the
   * Consultation stage's or IP case sheet's "Patient record" Dialog) rather
   * than as the standalone Patient records module. Hides the "browse other
   * patients" list and the Close button — the caller already handed us the
   * one patient they want and has its own way to dismiss the viewer (the
   * Dialog's close button/Escape) — and drops this component's own scroll
   * region and padding, since the Dialog supplies both.
   */
  embedded?: boolean;
}

export default function PatientRecords({ preselectUhid, onPreselectConsumed, onOpenVisit, onOpenAdmission, embedded }: PatientRecordsProps) {
  const isPhone = useIsPhone();
  const [listPage, setListPage] = useState(1);
  const [pageResult, setPageResult] = useState<PatientsPage | null>(null);
  const [loadingPage, setLoadingPage] = useState(true);
  const [sel, setSel]     = useState<Patient | null>(null);
  const [tab, setTab]     = useState<PatientRecordTabId>('overview');
  const [editPt, setEdit] = useState<Patient | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [deceasedOpen, setDeceasedOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [erasureSubmitting, setErasureSubmitting] = useState(false);
  const [erasureOpen, setErasureOpen] = useState(false);
  const [erasureError, setErasureError] = useState<string | null>(null);
  const [activeAdmission, setActiveAdmission] = useState<ActiveAdmission | null>(null);
  const { user } = useAuth();
  // Laboratory (the `laboratory` module) and Audit (`records:audit`, Admin by
  // default) are absent without their grants — see patientRecordTabs.ts. The
  // backend re-checks both.
  const tabs = patientRecordTabs(m => !!user?.modules?.includes(m));
  const activeTab: PatientRecordTabId = tabs.some(t => t.id === tab) ? tab : 'overview';
  // Recording (or correcting) a death is Admin/Doctor only — the backend
  // enforces the same rule itself (routes/patient-deceased.js).
  const canRecordDeath = user?.role === 'Admin' || user?.role === 'Doctor';

  // There is no patient search on this screen: finding a patient is the
  // topbar search (Ctrl+K, a search button on phones), which opens the record
  // here through `preselectUhid`. The landing list below is for browsing.
  useEffect(() => {
    if (embedded) { setLoadingPage(false); return; }
    setLoadingPage(true);
    getPatientsPage(listPage, PATIENTS_PAGE_SIZE).then(setPageResult).catch(() => {}).finally(() => setLoadingPage(false));
  }, [listPage, embedded]);

  // Surfaces an ongoing inpatient stay here even after the OP visit that led
  // to it has finished its own stages — the admission's lifecycle runs
  // independently in the IP/Wards module (see IPModule.tsx).
  useEffect(() => {
    if (!sel?.id) { setActiveAdmission(null); return; }
    getActiveAdmissionForPatient(sel.id).then(setActiveAdmission).catch(() => setActiveAdmission(null));
  }, [sel?.id]);

  // Deep-link from elsewhere in the app (topbar search, "View patient record"
  // during consultation, the IP case sheet).
  useEffect(() => {
    if (!preselectUhid) return;
    getPatient(preselectUhid).then(p => { setSel(p); setTab('overview'); }).catch(() => {});
    onPreselectConsumed?.();
  }, [preselectUhid]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectPatient = async (p: Patient) => {
    setTab('overview');
    try { setSel(await getPatient(p.uhid)); } catch { setSel(p as Patient); }
  };

  const refreshSelected = async () => {
    if (!sel) return;
    try { setSel(await getPatient(sel.uhid)); } catch {}
  };

  // Logs a DPDP erasure request on the patient's behalf — there is no patient
  // portal yet, so this is how one reaches the Admin review queue (Admin
  // Panel -> System -> Data Privacy -> Erasure Requests). Logging alone
  // never touches patient data; only an Admin's later approval + completion
  // does. See backend/routes/data-privacy.js.
  // The (optional) reason is asked in a dialog — window.prompt() is refused
  // by embedded browsers. A failure is shown in the dialog, which stays open.
  const requestErasure = () => { setErasureError(null); setErasureOpen(true); };
  const submitErasure = async (reason: string) => {
    if (!sel) return;
    setErasureSubmitting(true);
    setErasureError(null);
    try {
      await createErasureRequest(sel.uhid, reason || undefined);
      setErasureOpen(false);
      toast({ variant: 'success', title: 'Erasure request logged', description: 'An Admin will review it in Admin Panel → Data Privacy.' });
    } catch (e) {
      setErasureError(e instanceof Error ? e.message : 'Could not log the erasure request');
    } finally {
      setErasureSubmitting(false);
    }
  };

  const bloodGroup = sel?.vitals?.[0]?.blood_group || sel?.blood_group;

  return (
    <div className="fade-in" style={{
      display:'flex', flexDirection:'column', minHeight:0, minWidth:0,
      // Embedded mode sits inside a Dialog that already scrolls itself
      // (App.tsx's patient-record viewer) — a second fixed-height/overflow
      // region here would just nest two independent scrollbars.
      height: embedded ? 'auto' : (isPhone ? '100%' : 'calc(100vh - 130px)'),
      overflow: embedded ? 'visible' : 'hidden',
      border: (isPhone || embedded) ? 'none' : '1px solid #E5E7EB',
      borderRadius: (isPhone || embedded) ? 0 : 'var(--radius-lg)',
      background:'#fff',
    }}>
      {!sel ? (
        <PatientsListPage
          isPhone={isPhone}
          result={pageResult}
          loading={loadingPage}
          page={listPage}
          onPageChange={setListPage}
          onSelect={selectPatient}
        />
      ) : (
        <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, minHeight:0, overflow: embedded ? 'visible' : 'hidden' }}>
          {/* Patient header. Wraps rather than squeezing: the identity block
              takes the whole row when the actions can't sit beside it — the
              old no-wrap row pushed the Actions menu off the right edge of a
              tablet. */}
          <div style={{ padding: embedded ? '0 0 12px' : (isPhone ? '10px 12px' : '14px 18px'), borderBottom:'1px solid #E5E7EB', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
              <div style={{ width: isPhone ? 40 : 48, height: isPhone ? 40 : 48, borderRadius:'50%', background:'var(--teal)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize: isPhone ? 14 : 16, fontWeight:700, flexShrink:0 }}>
                {sel.name.split(' ').map(n=>n[0]).join('').slice(0,2)}
              </div>
              <div style={{ flex: isPhone ? '1 1 0' : '1 1 240px', minWidth:0 }}>
                <div style={{ fontSize: isPhone ? 16 : 17, fontWeight:700, marginBottom:4, overflowWrap:'anywhere' }}>
                  {patientDisplayName(sel)}
                  {sel.deceased && <Badge variant="neutral" className="ml-2 align-middle">Deceased</Badge>}
                </div>
                {isPhone ? (
                  // Phone: one line of identifiers instead of a column of chips.
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-start', gap:6 }}>
                    <div style={{ fontSize:12, color:'#4B5563' }}>
                      <span style={{ fontFamily:'var(--mono)', fontWeight:700, color:'var(--teal)' }}>{sel.uhid}</span>
                      {` · ${sel.gender}, ${sel.age ?? '—'}y`}{bloodGroup ? ` · ${bloodGroup}` : ''}{sel.mobile ? ` · ${sel.mobile}` : ''}
                    </div>
                    <ConsentButton uhid={sel.uhid} onOpen={() => setConsentOpen(true)} />
                  </div>
                ) : (
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                  {[sel.uhid, `${sel.gender}, ${sel.age ?? '—'}y`, sel.dob ? `DOB ${new Date(sel.dob).toLocaleDateString('en-IN')}` : null, `Blood group ${bloodGroup || '—'}`, sel.mobile ? `📱 ${sel.mobile}` : null].filter(Boolean).map((tag,i) => (
                    <span key={i} className="badge badge-gray" style={{ background:'#F3F4F6', color:'#4B5563' }}>{tag}</span>
                  ))}
                  <ConsentButton uhid={sel.uhid} onOpen={() => setConsentOpen(true)} />
                </div>
                )}
                {sel.address && !isPhone && <div style={{ fontSize:12, color:'#6B7280', marginTop:5, overflowWrap:'anywhere' }}>📍 {sel.address}</div>}
              </div>
              <div style={{ display:'flex', gap:6, flexShrink:0, marginLeft:'auto' }}>
                {/* Record actions live behind one menu instead of a row of
                    buttons. Same actions, same guards. */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="btn-ghost" title="Record actions" aria-label="Record actions"
                      style={{ fontSize:12, padding:'6px 10px', display:'inline-flex', alignItems:'center', gap:6 }}>
                      <Menu size={16} aria-hidden />{!isPhone && ' Actions'}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" collisionPadding={8} style={{ minWidth:230 }}>
                    <DropdownMenuItem onSelect={() => setMergeOpen(true)}>🔗 Merge</DropdownMenuItem>
                    {/* Disabled once redacted (pii_erased_at set) — the backend
                        itself refuses this too (PATCH /:uhid returns 409
                        PII_ERASED), so this is UI courtesy, not the real gate. */}
                    <DropdownMenuItem disabled={!!sel.pii_erased_at} onSelect={() => setEdit(sel)}>
                      ✏ Edit record
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled={erasureSubmitting || !!sel.pii_erased_at} onSelect={requestErasure}>
                      🛡 Request erasure
                    </DropdownMenuItem>
                    {canRecordDeath && (
                      <>
                        {/* Separated: the one action here that changes how every
                            other module treats this patient. */}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => setDeceasedOpen(true)}>
                          🕊 {sel.deceased ? 'Correct deceased status' : 'Record as deceased'}
                        </DropdownMenuItem>
                      </>
                    )}
                    {sel.pii_erased_at && (
                      <div style={{ padding:'6px 8px 4px', fontSize:11, color:'#6B7280', maxWidth:230 }}>
                        Redacted under a DPDP erasure request — details can no longer be edited.
                      </div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
                {/* Close stays a visible button: leaving the record is
                    navigation. Not shown embedded — the host Dialog closes it. */}
                {!embedded && (
                  <button className="btn-ghost" title="Close and return to patients list" aria-label="Close record" style={{ fontSize:12, padding:'6px 12px' }} onClick={() => setSel(null)}>
                    {isPhone ? '✕' : '✕ Close'}
                  </button>
                )}
              </div>
            </div>
            {sel.deceased && (
              <div className="mt-2.5 rounded-[var(--radius)] border border-border bg-muted px-3 py-2 text-[12.5px] font-semibold text-ink">
                Deceased — {sel.deceased_at ? new Date(sel.deceased_at).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : 'date not recorded'}
                <span className="font-normal text-ink-muted">
                  {sel.deceased_recorded_by ? ` · recorded by ${sel.deceased_recorded_by}` : ''} · appointments cannot be booked and automated reminders are suppressed
                </span>
              </div>
            )}
            {activeAdmission && (
              <div style={{ marginTop:10, padding:'8px 12px', background:'#DBEAFE', border:'1px solid #93C5FD', borderRadius:'var(--radius)', fontSize:12.5, color:'#1D4ED8', fontWeight:600 }}>
                🛏 Currently admitted — {activeAdmission.ward_name} · Bed {activeAdmission.bed_number} (since {new Date(activeAdmission.admitted_at).toLocaleDateString('en-IN')})
              </div>
            )}
          </div>

          {/* Tabs — what fits sits in the strip, the rest is behind More, so a
              tab is never off-screen to the right (patientRecordTabs.ts). */}
          <div style={{ padding: embedded ? '6px 0 0' : (isPhone ? '6px 8px 0' : '6px 14px 0'), flexShrink:0, background:'#fff' }}>
            <PriorityTabNav
              tabs={tabs}
              active={activeTab}
              onSelect={setTab}
              ariaLabel="Patient record sections"
              coreLabel="Clinical"
              secondaryLabel="Records & admin"
            />
          </div>

          {/* Tab content */}
          <div style={{ flex:1, minHeight:0, minWidth:0, overflowY: embedded ? 'visible' : 'auto', overflowX:'hidden', WebkitOverflowScrolling:'touch', padding: embedded ? '14px 0 0' : (isPhone ? '14px 12px' : '16px 18px') }}>
            {activeTab === 'overview'      && <OverviewTab patient={sel} />}
            {activeTab === 'medications'   && <PatientHistoryPanel kind="medications" uhid={sel.uhid} editable />}
            {activeTab === 'medical'       && <MedicalHistoryTabs uhid={sel.uhid} editable kinds={['diagnoses', 'procedures']} />}
            {activeTab === 'timeline'      && <TimelineTab uhid={sel.uhid} isPhone={isPhone} />}
            {activeTab === 'vitals'        && <VitalsTrendTab patient={sel} />}
            {activeTab === 'lab'           && <PatientLabHistory uhid={sel.uhid} />}
            {activeTab === 'docs'          && <DocumentsTab uhid={sel.uhid} consultations={sel.consultations || []} onChanged={refreshSelected} />}
            {activeTab === 'prescriptions' && <PatientPrescriptionHistory uhid={sel.uhid} />}
            {activeTab === 'visits'        && <VisitsTab uhid={sel.uhid} onOpenVisit={onOpenVisit} />}
            {activeTab === 'admissions'    && <PatientAdmissionsTab patientId={sel.id} uhid={sel.uhid} onOpenAdmission={onOpenAdmission} />}
            {activeTab === 'history'       && <HistoryTab consultations={sel.consultations || []} onChanged={refreshSelected} />}
            {activeTab === 'billing'       && <BillingTab patientId={sel.id} />}
            {activeTab === 'audit'         && <PatientAuditTab uhid={sel.uhid} />}
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editPt && <EditModal patient={editPt} onClose={() => setEdit(null)} onSaved={async () => { setEdit(null); await refreshSelected(); }} />}

      {consentOpen && sel && <ConsentDialog uhid={sel.uhid} patientName={sel.name} onClose={() => setConsentOpen(false)} />}

      {deceasedOpen && sel && (
        <DeceasedStatusDialog
          patient={sel}
          onClose={() => setDeceasedOpen(false)}
          onChanged={async () => { setDeceasedOpen(false); await refreshSelected(); }}
        />
      )}

      {mergeOpen && sel && <MergePatientsModal patientA={sel} onClose={() => setMergeOpen(false)} onSubmitted={() => setMergeOpen(false)} />}

      <TextPromptDialog
        open={erasureOpen && !!sel}
        title={sel ? `Log a data-erasure request for ${sel.name} (${sel.uhid})?` : ''}
        description="This only logs the request. Nothing is changed until an Admin approves and completes it in Admin Panel → Data Privacy."
        label="Reason"
        optional
        multiline
        confirmLabel="Log request"
        pending={erasureSubmitting}
        error={erasureError}
        onConfirm={(reason) => { void submitErasure(reason); }}
        onClose={() => setErasureOpen(false)}
      />
    </div>
  );
}

// The registry landing view: every patient, newest-registered first,
// PATIENTS_PAGE_SIZE per page — replaces the old "today's activity" cards,
// which hid every patient without activity today behind the search bar.
function PatientsListPage({ isPhone, result, loading, page, onPageChange, onSelect }: {
  isPhone: boolean;
  result: PatientsPage | null;
  loading: boolean;
  page: number;
  onPageChange: (p: number) => void;
  onSelect: (p: Patient) => void;
}) {
  const rows = result?.data ?? [];
  const total = result?.total ?? null;
  const totalPages = total != null ? Math.max(1, Math.ceil(total / PATIENTS_PAGE_SIZE)) : null;
  const rangeStart = rows.length ? (page - 1) * PATIENTS_PAGE_SIZE + 1 : 0;
  const rangeEnd = (page - 1) * PATIENTS_PAGE_SIZE + rows.length;

  return (
    <div style={{ flex:1, overflowY:'auto', background:'#F9FAFB' }}>
      <div style={{ padding: isPhone ? '16px 14px' : '20px 24px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', flexWrap:'wrap', gap:8, marginBottom:12 }}>
          <div style={{ fontSize:12, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'.5px' }}>
            All patients
          </div>
          {total != null && (
            <div style={{ fontSize:12, color:'#9CA3AF' }}>
              {rows.length ? `Showing ${rangeStart}–${rangeEnd} of ${total}` : `${total} patient${total===1?'':'s'}`}
            </div>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign:'center', padding:30, color:'#9CA3AF', fontSize:13 }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px 20px' }}>
            <div style={{ fontSize:36, marginBottom:8 }}>🏥</div>
            <div style={{ fontSize:15, fontWeight:600 }}>No patients registered yet</div>
          </div>
        ) : (
          <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:'var(--radius)', overflow:'hidden' }}>
            {!isPhone && (
              <div style={{ display:'grid', gridTemplateColumns:'130px 1fr 120px 110px 130px 130px', gap:10, padding:'9px 14px', background:'#F9FAFB', borderBottom:'1px solid #E5E7EB', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'.5px' }}>
                <div>UHID</div>
                <div>Name</div>
                <div>Contact number</div>
                <div>Age / Gender</div>
                <div>Registered date</div>
                <div>Last visit</div>
              </div>
            )}
            {rows.map(p => isPhone ? (
              // Phone: only what identifies the patient — name, UHID, age/sex.
              // Contact number and dates are on the record itself, one tap away.
              <button key={p.uhid} type="button" onClick={() => onSelect(p)}
                style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'10px 12px', background:'none', border:'none', borderBottom:'1px solid #F3F4F6', borderRadius:0, cursor:'pointer', textAlign:'left', fontSize:13 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{patientDisplayName(p)}</div>
                  <div style={{ fontSize:12, color:'#6B7280', marginTop:2 }}>
                    <span style={{ color:'var(--teal)', fontWeight:700, fontFamily:'var(--mono)' }}>{p.uhid}</span>
                    {[p.gender, p.age != null ? `${p.age}y` : null].filter(Boolean).length > 0 && ` · ${[p.gender, p.age != null ? `${p.age}y` : null].filter(Boolean).join(', ')}`}
                  </div>
                </div>
                <span aria-hidden style={{ color:'#9CA3AF' }}>›</span>
              </button>
            ) : (
              <div key={p.uhid} onClick={() => onSelect(p)}
                style={{ display:'grid', gridTemplateColumns:'130px 1fr 120px 110px 130px 130px', gap:10, padding:'11px 14px', borderBottom:'1px solid #F3F4F6', cursor:'pointer', fontSize:13 }}
                onMouseEnter={e => e.currentTarget.style.background='#F9FAFB'} onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <button
                  onClick={e => { e.stopPropagation(); onSelect(p); }}
                  style={{ background:'none', border:'none', padding:0, color:'var(--teal)', fontWeight:700, fontFamily:'var(--mono)', fontSize:12.5, cursor:'pointer', textDecoration:'underline', textAlign:'left', width:'fit-content' }}>
                  {p.uhid}
                </button>
                <div style={{ fontWeight:600, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{patientDisplayName(p)}</div>
                <div style={{ color:'#6B7280' }}>{p.mobile || '—'}</div>
                <div style={{ color:'#6B7280' }}>{p.gender}{p.age != null ? `, ${p.age}y` : ''}</div>
                <div style={{ color:'#6B7280', fontSize:12 }}>
                  {p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : '—'}
                </div>
                <div style={{ color:'#6B7280', fontSize:12 }}>
                  {p.last_visit_at ? new Date(p.last_visit_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : '—'}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && rows.length > 0 && (totalPages == null || totalPages > 1) && (
          <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:12, marginTop:16 }}>
            <button className="btn-ghost" style={{ padding:'6px 14px', fontSize:12.5 }} disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              ← Previous
            </button>
            <span style={{ fontSize:12.5, color:'#6B7280' }}>
              Page {page}{totalPages != null ? ` of ${totalPages}` : ''}
            </span>
            <button className="btn-ghost" style={{ padding:'6px 14px', fontSize:12.5 }}
              disabled={totalPages != null ? page >= totalPages : !result?.has_more}
              onClick={() => onPageChange(page + 1)}>
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Long-term vitals history (KMC #34) across every visit and admission, from
// the same vitals rows GET /api/patients/:uhid already returns (current
// versions only — the server filters superseded amendments). Plotted at time
// taken, in °F to match the rest of this screen; the advisory OPD ranges are
// drawn only for adults, since they are adult ranges.
function VitalsTrendTab({ patient }: { patient: Patient }) {
  const adult = isAdult(patient.age, patient.dob);
  return (
    <div className="fade-in">
      <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.5px', color:'#6B7280', marginBottom:10 }}>
        Vitals over time — all visits and admissions
      </div>
      <VitalsTrendChart
        readings={patient.vitals || []}
        tempUnit="F"
        height={260}
        referenceLines={m => (adult ? opdReferenceLines(m, 'F') : [])}
        referenceNote={adult
          ? 'Dashed lines: advisory ranges only — not a diagnosis.'
          : `No reference lines: the advisory ranges are adult ranges, and this patient is under ${ADULT_MIN_AGE} or has no recorded age.`}
      />
    </div>
  );
}

function OverviewTab({ patient }: { patient: Patient }) {
  const isPhone = useIsPhone();
  const v = patient.vitals?.[0] || {} as PatientVitals;
  // The latest row may be an IP Case Sheet reading, stored in °C; OP rows are
  // °F. Shown in °F like the trend chart, and checked on that scale.
  const tempF = v.temperature != null && !isNaN(Number(v.temperature)) ? normalizeTemp(Number(v.temperature), 'F') : undefined;
  const vitals = [
    // Ranges from lib/vitalThresholds.ts — the one module every screen showing
    // a vital reads, so a reading flagged at Vitals or Pre-Consult still reads
    // flagged here. This screen previously carried its own copy that had
    // drifted (BMI > 25 vs >= 30, temp > 99 vs > 99.5); see that file's header.
    { l:'Blood pressure', s:'BP', val:`${v.bp_systolic||'—'}/${v.bp_diastolic||'—'}`, u:'mmHg', warn:checkBp(v.bp_systolic, v.bp_diastolic).warn },
    { l:'Pulse rate',     s:'Pulse', val:v.pulse_rate||'—', u:'bpm', warn:checkPulse(v.pulse_rate).warn },
    { l:'SpO₂',          s:'SpO₂', val:v.spo2||'—',  u:'%',   warn:checkSpo2(v.spo2).warn },
    { l:'Temperature',   s:'Temp', val:tempF ?? '—',  u:'°F',  warn:checkTemp(tempF).warn },
    { l:'Weight',        s:'Weight', val:v.weight_kg||'—', u:'kg' },
    { l:'BMI',           s:'BMI', val:v.bmi||'—',   u:'kg/m²', warn:checkBmi(v.bmi).warn },
    // Falls back to the persisted patient-level value (e.g. corrected via
    // Edit record, or the vitals row predates the column). Blood group is not
    // repeated here — it's in the patient header above every tab.
    { l:'Height',        s:'Height', val:v.height_cm || patient.height_cm || '—', u:'cm' },
  ];
  return (
    <div className="fade-in">
      <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.5px', color:'#6B7280', marginBottom:10 }}>Latest vitals</div>
      {/* auto-fit/minmax instead of a fixed 4 columns — on a narrow phone,
          repeat(4,1fr) forced each card below its content's natural width,
          overflowing the tab content horizontally. That mattered beyond
          looks: a touch swipe starting over a horizontally-scrollable area
          can get axis-locked to that scroll, which reads as "can't scroll
          the page down" even though the page itself scrolls fine elsewhere. */}
      {isPhone ? (
        // Phone: three compact tiles per row, value and unit on one line, so
        // all seven readings fit in about a third of the screen.
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3, minmax(0, 1fr))', gap:6, marginBottom:16 }}>
          {vitals.map(it => (
            <div key={it.l} title={it.l} style={{ minWidth:0, background:'#F9FAFB', border:`1px solid ${it.warn?'#FCA5A5':'#E5E7EB'}`, borderLeft:`3px solid ${it.warn?'#F87171':'#A7F3D0'}`, borderRadius:'var(--radius)', padding:'6px 8px' }}>
              <div style={{ fontSize:9.5, fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', color:'#6B7280' }}>{it.s}{it.warn?' ⚠':''}</div>
              <div style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                <span style={{ fontSize:14.5, fontWeight:700, color:it.warn?'#DC2626':'#111827' }}>{it.val}</span>
                {it.val !== '—' && <span style={{ fontSize:10, color:'#6B7280', marginLeft:3 }}>{it.u}</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(130px, 1fr))', gap:10, marginBottom:18 }}>
        {vitals.map(it => (
          <div key={it.l} style={{ minWidth:0, background:'#F9FAFB', border:`1px solid ${it.warn?'#FCA5A5':'#E5E7EB'}`, borderLeft:`3px solid ${it.warn?'#F87171':'#A7F3D0'}`, borderRadius:'var(--radius)', padding:'10px 12px' }}>
            <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.5px', color:'#6B7280', marginBottom:4 }}>{it.l}</div>
            <div style={{ fontSize:18, fontWeight:700, color:it.warn?'#DC2626':'#111827' }}>{it.val}</div>
            <div style={{ fontSize:11, color:'#6B7280' }}>{it.u}{it.warn?' ⚠':''}</div>
          </div>
        ))}
      </div>
      )}

      <AllergySummary uhid={patient.uhid} />

      <AISummaryPanel uhid={patient.uhid} variant="full" />

      {v.symptoms && <>
        <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.5px', color:'#6B7280', marginBottom:8, marginTop:18 }}>Chief complaints</div>
        <div style={{ background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:'var(--radius)', padding:'11px 14px', fontSize:13, color:'#374151', lineHeight:1.7, fontStyle:'italic', marginBottom:16 }}>"{v.symptoms}"</div>
      </>}
      {!v.recorded_at && (
        <div style={{ textAlign:'center', padding:24, color:'#9CA3AF', fontSize:13 }}>No vitals recorded yet</div>
      )}
    </div>
  );
}

// Read-only glance at the same structured, tenant-wide allergies table
// AllergyPanel (features/consultation) captures into — recording/editing
// stays there (and in the IP Case Sheet's assessment), so this never forks
// into a second write path for the same data. Placed directly below Latest
// vitals: both are "what any clinician needs before touching this patient",
// and an allergy is exactly the kind of fact that shouldn't require opening
// a consultation to rediscover.
function AllergySummary({ uhid }: { uhid: string }) {
  const { data: allergies, isLoading } = useAllergies(uhid);
  if (isLoading) return null; // no spinner — this sits ahead of AISummaryPanel's own loading state, not worth stacking two

  const active = (allergies || []).filter(a => a.clinical_status === 'active');

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: '#6B7280', marginBottom: 8 }}>
        Allergies
      </div>
      {/* Silence must never read as "confirmed safe" — an empty list here
          could mean either "reviewed, none found" or "nobody has asked yet",
          and those are different clinical facts. Say so explicitly rather
          than just showing nothing, the same distinction AllergyPanel and
          the IP Case Sheet's assessment already make. */}
      {!active.length && (
        <div style={{ fontSize: 12.5, color: '#9CA3AF', fontStyle: 'italic' }}>
          No allergies recorded — not necessarily reviewed.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {active.map(a => (
          <div key={a.id} style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            background: a.criticality === 'high' ? '#FEF2F2' : '#F9FAFB',
            border: `1px solid ${a.criticality === 'high' ? '#FECACA' : '#E5E7EB'}`,
            borderLeft: `3px solid ${a.criticality === 'high' ? '#F87171' : '#D1D5DB'}`,
            borderRadius: 'var(--radius)', padding: '8px 12px', fontSize: 13,
          }}>
            <span style={{ fontWeight: 600, color: '#111827' }}>{a.substance}</span>
            {a.criticality && (
              <Badge variant={a.criticality === 'high' ? 'danger' : a.criticality === 'unable-to-assess' ? 'warn' : 'neutral'}>
                {a.criticality}
              </Badge>
            )}
            {a.category && <span style={{ fontSize: 11, color: '#9CA3AF' }}>{a.category}</span>}
            {a.reaction && <span style={{ fontSize: 12, color: '#6B7280' }}>· Reaction: {a.reaction}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// Patient consent (patient_consents — see ISS-05/MVP-43 for the original
// AI-processing one, and migration_patient_consent_registration.sql for the
// Treatment/Data Sharing ones the Registration step captures alongside it).
//
// Captured once per patient, not per visit, so this is where a consent is
// reviewed, renewed after it expires, or withdrawn — the front desk or any
// clinician can act here rather than needing a consultation open.
//
// One chip in the patient header rather than a row per consent form on the
// Overview: the forms are reviewed occasionally, while the Overview is read on
// every open. The chip still says when something needs attention (never asked,
// expired, or a mandatory consent standing declined — `due`), so an unrecorded
// AI-processing consent, which hard-blocks AI document generation, doesn't go
// quiet just because it moved.
//
// Withdrawing does not delete anything: it records a new "declined" answer and
// leaves the previous one in the patient's consent history, with the exact text
// and version they had agreed to.
function ConsentButton({ uhid, onOpen }: { uhid: string; onOpen: () => void }) {
  const { data } = useConsentRequirements(uhid);
  const items = data?.items ?? [];
  const due = items.filter(i => i.due).length;
  const granted = items.filter(i => i.status === 'granted').length;
  const label = !data ? 'Consents'
    : due > 0 ? `Consents · ${due} need${due === 1 ? 's' : ''} attention`
    : `Consents · ${granted}/${items.length} given`;
  return (
    <button
      type="button"
      onClick={onOpen}
      title="Review, record, renew or withdraw this patient's consents"
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11.5px] font-semibold transition-colors pointer-coarse:min-h-9 ${
        due > 0
          ? 'border-warn/40 bg-warn-soft text-warn-ink hover:bg-warn-soft/70'
          : 'border-border bg-card text-teal hover:bg-muted'
      }`}
    >
      {due > 0 ? '⚠' : '📋'} {label}
    </button>
  );
}

function ConsentDialog({ uhid, patientName, onClose }: { uhid: string; patientName: string; onClose: () => void }) {
  const { data, isLoading } = useConsentRequirements(uhid);
  const recordOne = useRecordConsent(uhid);
  const [savingType, setSavingType] = useState<ConsentTypeId | null>(null);

  const record = async (type: ConsentTypeId, granted: boolean) => {
    setSavingType(type);
    try { await recordOne.mutateAsync({ type, granted }); }
    catch (e) { toast({ variant: 'danger', title: 'Could not save the consent', description: e instanceof Error ? e.message : undefined }); }
    finally { setSavingType(null); }
  };

  return (
    <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[88vh] w-[calc(100vw-24px)] max-w-[640px] grid-cols-[minmax(0,1fr)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Consents</DialogTitle>
          <DialogDescription>{patientName} — asked once per patient; renew when expired, or record a withdrawal.</DialogDescription>
        </DialogHeader>
        {isLoading || !data ? (
          <div className="py-6 text-center text-[13px] text-ink-muted">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="py-6 text-center text-[13px] text-ink-muted">No consent forms are enabled for this hospital.</div>
        ) : (
          <div className="flex flex-col gap-2">
            {data.items.map(item => {
              const granted = item.status === 'granted';
              const attention = item.due;
              return (
                <div key={item.consent_type} className={`rounded-[var(--radius)] border border-l-[3px] px-3 py-2.5 text-[13px] ${
                  attention ? 'border-warn/40 border-l-warn bg-warn-soft' : granted ? 'border-border border-l-success bg-muted/40' : 'border-border bg-muted/40'
                }`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-ink">{item.label}</span>
                    {item.required && <Badge variant="neutral">Mandatory</Badge>}
                    <ConsentStatusBadge status={item.status} />
                    <div className="ml-auto">
                      {!granted ? (
                        <button className="btn-ghost text-[12px]" disabled={savingType === item.consent_type} onClick={() => record(item.consent_type, true)}>
                          {item.status === 'expired' ? 'Renew consent' : 'Record consent'}
                        </button>
                      ) : (
                        <button className="btn-ghost text-[12px]" disabled={savingType === item.consent_type} onClick={() => record(item.consent_type, false)}>
                          Withdraw consent
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-1 text-[12px] text-ink-muted">
                    {item.current
                      ? `Recorded by ${item.current.granted_by || '—'}`
                        + (item.current.recorded_at ? ` on ${new Date(item.current.recorded_at).toLocaleDateString('en-IN')}` : '')
                        + (item.current.version_on_file ? ` · version ${item.current.version_on_file}` : '')
                        + (item.current.expires_at ? ` · expires ${new Date(item.current.expires_at).toLocaleDateString('en-IN')}` : '')
                      : item.description}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TimelineTab({ uhid, isPhone }: { uhid: string; isPhone: boolean }) {
  const [data, setData]       = useState<TimelineData | null>(null);
  const [generating, setGen]  = useState(false);
  const [error, setError]     = useState('');
  const [severityFilter, setSeverityFilter] = useState('critical'); // all | critical | watch | routine

  const load = () => getPatientTimeline(uhid).then(setData).catch(e => setError(e instanceof Error ? e.message : String(e)));
  useEffect(() => { setData(null); setError(''); load(); }, [uhid]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    setGen(true); setError('');
    try {
      const aiCfg = loadConfig();
      setData(await generatePatientTimeline(uhid, aiCfg.provider, getActiveModel(aiCfg)));
    }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setGen(false);
  };

  if (error) return (
    <div style={{ padding:20 }}>
      <div style={{ color:'#DC2626', fontSize:13, marginBottom:12 }}>Error: {error}</div>
      <button className="btn-primary" onClick={generate}>Retry</button>
    </div>
  );

  if (!data) return <div style={{ textAlign:'center', padding:36, color:'#6B7280' }}>Loading timeline…</div>;

  if (!data.events) return (
    <div style={{ textAlign:'center', padding:'40px 20px' }}>
      <div style={{ fontSize:44, marginBottom:12 }}>📈</div>
      <div style={{ fontSize:15, fontWeight:700, marginBottom:8 }}>Clinical timeline</div>
      <div style={{ fontSize:13, color:'#6B7280', marginBottom:20, lineHeight:1.6, maxWidth:460, margin:'0 auto 20px' }}>
        Builds a chronological timeline of clinically significant milestones — diagnoses, admissions, procedures, allergies, red flags — extracted from every visit, vitals record, consultation note, and uploaded document for this patient. Unlike Medical history (the editable diagnosis and procedure lists), this is an AI read-across of the whole record; building it also tops up those lists with anything new it finds.
      </div>
      <button className="btn-primary" onClick={generate} disabled={generating}>
        {generating ? '⏳ Analysing records…' : '🔍 Build timeline'}
      </button>
    </div>
  );

  const events = data.events;
  if (events.length === 0) return (
    <div style={{ textAlign:'center', padding:36, color:'#9CA3AF' }}>
      No clinically significant milestones found in this patient's records yet.
      <div style={{ marginTop:12 }}><button className="btn-ghost" onClick={generate} disabled={generating}>↺ Re-check</button></div>
    </div>
  );

  const filtered = severityFilter === 'all' ? events : events.filter(e => e.severity === severityFilter);
  const displayed = [...filtered].reverse(); // newest first

  // Bucket consecutive same-year events together (displayed is already
  // sorted newest-first, so same-year events are contiguous) — this drives
  // both the in-flow sticky year dividers and the quick-jump sidebar, so a
  // doctor scanning years of history can tell at a glance how much happened
  // in any given year instead of just seeing an unbroken event stream.
  const groups: { year: number | null; events: TimelineEvent[] }[] = [];
  for (const ev of displayed) {
    const year = ev.date ? new Date(ev.date).getFullYear() : null;
    const last = groups[groups.length - 1];
    if (last && last.year === year) last.events.push(ev);
    else groups.push({ year, events: [ev] });
  }
  const yearCounts: Record<number, number> = {};
  displayed.forEach(ev => { const y = ev.date ? new Date(ev.date).getFullYear() : null; if (y) yearCounts[y] = (yearCounts[y] || 0) + 1; });
  const years = [...new Set(groups.map(g => g.year).filter((y): y is number => y != null))]; // most-recent first, from `displayed`'s order

  const counts: Record<'critical' | 'watch' | 'routine', number> = { critical:0, watch:0, routine:0 };
  events.forEach(e => { if (counts[e.severity] !== undefined) counts[e.severity]++; });

  return (
    <div className="fade-in">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6, flexWrap:'wrap', gap:10 }}>
        <span className="badge badge-success" style={{ fontSize:12 }}>✓ AI-generated · For clinical reference only</span>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {data.generated_at && <span style={{ fontSize:11, color:'#9CA3AF' }}>Generated {new Date(data.generated_at).toLocaleString('en-IN')}</span>}
          <button className="btn-ghost" style={{ padding:'5px 12px', fontSize:12 }} onClick={generate} disabled={generating}>
            {generating ? '⏳ Refreshing…' : '↺ Regenerate'}
          </button>
        </div>
      </div>

      {data.stale && (
        <div style={{ margin:'10px 0', padding:'8px 12px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:'var(--radius)', fontSize:12, color:'#92400E' }}>
          ⚠ New records have been added since this timeline was generated — click Regenerate to include them.
        </div>
      )}

      {/* Severity filter chips */}
      <div style={{ display:'flex', gap:8, margin:'14px 0 18px', flexWrap:'wrap' }}>
        {([
          ['all', `All (${events.length})`, null],
          ['critical', `Critical (${counts.critical})`, TL_SEVERITY.critical],
          ['watch', `Watch (${counts.watch})`, TL_SEVERITY.watch],
          ['routine', `Routine (${counts.routine})`, TL_SEVERITY.routine],
        ] as [string, string, typeof TL_SEVERITY[string] | null][]).map(([key, label, sev]) => (
          <div key={key} onClick={() => setSeverityFilter(key)}
            style={{
              padding:'5px 13px', borderRadius:20, cursor:'pointer', fontSize:12, fontWeight:600, transition:'all .15s',
              background: severityFilter===key ? (sev ? sev.bg : '#111827') : '#F3F4F6',
              color: severityFilter===key ? (sev ? sev.text : '#fff') : '#6B7280',
              border: `2px solid ${severityFilter===key ? (sev ? sev.border : '#111827') : 'transparent'}`,
            }}>
            {label}
          </div>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:28, color:'#9CA3AF', fontSize:13 }}>No events match this filter.</div>
      ) : (
        <div style={{ display:'flex', gap:16 }}>
          {years.length > 1 && !isPhone && (
            <div style={{ flexShrink:0, width:64, position:'sticky', top:0, alignSelf:'flex-start' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>Jump to year</div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {years.map(y => (
                  <button key={y} onClick={() => document.getElementById(`tl-year-${y}`)?.scrollIntoView({ behavior:'smooth', block:'start' })}
                    style={{ padding:'6px 4px', borderRadius:'var(--radius)', border:'1px solid #E5E7EB', background:'#F9FAFB', fontSize:12, fontWeight:700, color:'#374151', cursor:'pointer', lineHeight:1.3 }}>
                    {y}
                    <div style={{ fontSize:9.5, fontWeight:600, color:'#9CA3AF' }}>{yearCounts[y]} evt{yearCounts[y]!==1?'s':''}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{ position:'relative', paddingLeft:4, flex:1, minWidth:0 }}>
          <div style={{ position:'absolute', left:19, top:6, bottom:6, width:2, background:'#E5E7EB' }} />
          {groups.map((g, gi) => (
            <div key={gi}>
              {g.year ? (
                // Sticky divider between years — sits flush white so it visually
                // masks the connecting line as it scrolls under it, giving a
                // clean break a doctor can use to tell "which year am I in"
                // at a glance while scanning years of history.
                <div id={`tl-year-${g.year}`} style={{ position:'sticky', top:0, zIndex:2, background:'#fff', paddingTop:gi===0?0:10, paddingBottom:8 }}>
                  <div style={{ display:'flex', alignItems:'baseline', gap:8, paddingBottom:8, borderBottom:'2px solid #111827' }}>
                    <span style={{ fontSize:19, fontWeight:800, color:'#111827' }}>{g.year}</span>
                    <span style={{ fontSize:12, fontWeight:600, color:'#9CA3AF' }}>{g.events.length} event{g.events.length!==1?'s':''}</span>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize:11, fontWeight:600, color:'#9CA3AF', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:10 }}>Undated</div>
              )}
              {g.events.map((ev, i) => {
                const cat = (TL_CATEGORY[ev.category] || TL_CATEGORY.other)!;
                const sev = (TL_SEVERITY[ev.severity] || TL_SEVERITY.routine)!;
                return (
                  <div key={i} style={{ display:'flex', gap:14, marginTop:16, marginBottom:g.events.length-1===i?24:0, position:'relative' }}>
                    <div style={{
                      width:38, height:38, borderRadius:'50%', flexShrink:0, zIndex:1,
                      background: sev.bg, border:`2px solid ${sev.dot}`,
                      display:'flex', alignItems:'center', justifyContent:'center', fontSize:16,
                      boxShadow: ev.severity==='critical' ? `0 0 0 4px rgba(220,38,38,.12)` : 'none',
                    }}>
                      {cat.icon}
                    </div>
                    <div className="card" style={{ flex:1, padding:'12px 14px', borderLeft:`3px solid ${sev.dot}`, minWidth:0 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10, marginBottom:5 }}>
                        <div style={{ fontWeight:700, fontSize:14, color:'#111827' }}>{ev.title}</div>
                        <span style={{ fontSize:11, color:'#9CA3AF', whiteSpace:'nowrap', flexShrink:0 }}>
                          {ev.date ? new Date(ev.date).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : '—'}
                        </span>
                      </div>
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:7 }}>
                        <span style={{ fontSize:10.5, fontWeight:700, padding:'2px 8px', borderRadius:10, background:sev.bg, color:sev.text, border:`1px solid ${sev.border}` }}>
                          {sev.label.toUpperCase()}
                        </span>
                        <span style={{ fontSize:10.5, fontWeight:600, padding:'2px 8px', borderRadius:10, background:'#F3F4F6', color:'#4B5563' }}>
                          {cat.icon} {cat.label}
                        </span>
                      </div>
                      {ev.summary && <div style={{ fontSize:13, color:'#374151', lineHeight:1.55, marginBottom:ev.source?6:0 }}>{ev.summary}</div>}
                      {ev.source && <div style={{ fontSize:11, color:'#9CA3AF', fontStyle:'italic' }}>Source: {ev.source}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VisitsTab({ uhid, onOpenVisit }: { uhid: string; onOpenVisit?: (visitId: string) => void }) {
  const [visits, setVisits] = useState<Visit[] | null>(null);
  const [error, setError]   = useState('');
  const [open, setOpen]     = useState<Record<number, boolean>>({});

  useEffect(() => {
    setVisits(null);
    getVisitsForPatient(uhid).then(setVisits).catch(e => setError(e instanceof Error ? e.message : String(e)));
  }, [uhid]);

  if (error) return <div style={{ padding:20, color:'#DC2626', fontSize:13 }}>Error: {error}</div>;
  if (!visits) return <div style={{ textAlign:'center', padding:36, color:'#6B7280' }}>Loading visits…</div>;
  if (visits.length === 0) return <div style={{ textAlign:'center', padding:36, color:'#6B7280' }}>No visits recorded yet</div>;

  return (
    <div className="fade-in">
      {visits.map(visit => {
        const isOpen = open[visit.id] ?? false;
        return (
          <div key={visit.id} className="card" style={{ marginBottom:12, padding:0, overflow:'hidden' }}>
            <div onClick={() => setOpen(p => ({ ...p, [visit.id]: !isOpen }))}
              style={{ padding:'12px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:8, flexWrap:'wrap', cursor:'pointer', background:'#F9FAFB' }}>
              <div style={{ minWidth:0 }}>
                <div style={{ fontWeight:700, fontSize:13, fontFamily:'var(--mono)' }}>{visit.visit_id}</div>
                <div style={{ fontSize:11, color:'#6B7280', marginTop:2 }}>
                  {new Date(visit.started_at).toLocaleString('en-IN')} · {visit.visit_type} · {visit.is_new_patient ? 'New patient' : 'Returning patient'}
                </div>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginLeft:'auto' }}>
                <span className={`badge ${visit.current_stage==='done'?'badge-success':'badge-info'}`}>{visit.current_stage}</span>
                {visit.resolution && <span className="badge badge-gray" style={{ background:'#F3F4F6', color:'#4B5563' }}>{visit.resolution}</span>}
                {onOpenVisit && (
                  <button className="btn-ghost" style={{ padding:'3px 10px', fontSize:11.5 }}
                    onClick={e => { e.stopPropagation(); onOpenVisit(visit.visit_id); }}>
                    🔗 Open workflow →
                  </button>
                )}
                <span style={{ fontSize:12, color:'#9CA3AF' }}>{isOpen ? '▲' : '▼'}</span>
              </div>
            </div>
            {isOpen && (
              <div style={{ padding:'12px 16px' }}>
                {visit.stages.filter(s => s.status !== 'pending').map(s => (
                  <StageRow key={s.id} stage={s} />
                ))}
                {visit.resolution_notes && (
                  <div style={{ marginTop:8, fontSize:12, color:'#374151', fontStyle:'italic' }}>Resolution notes: "{visit.resolution_notes}"</div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BillingTab({ patientId }: { patientId: number }) {
  const [bills, setBills]   = useState<Bill[] | null>(null);
  const [error, setError]   = useState('');
  const [marking, setMarking] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);

  const load = () => getPatientBills(patientId).then(setBills).catch(e => setError(e instanceof Error ? e.message : String(e)));
  useEffect(() => { setBills(null); load(); }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const markPaid = async (id: number) => {
    setMarking(id);
    try { await markBillPaid(id); await load(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setMarking(null);
  };

  const viewPdf = async (id: number) => {
    setPdfLoading(id);
    try { await openBillPdf(id); } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setPdfLoading(null);
  };

  if (error) return <div style={{ padding:20, color:'#DC2626', fontSize:13 }}>Error: {error}</div>;
  if (!bills) return <div style={{ textAlign:'center', padding:36, color:'#6B7280' }}>Loading bills…</div>;
  if (bills.length === 0) return <div style={{ textAlign:'center', padding:36, color:'#6B7280' }}>No bills recorded yet</div>;

  const totalDue = bills.filter(b => b.payment_status !== 'Paid').reduce((s, b) => s + parseFloat(String(b.total_amount)), 0);

  return (
    <div className="fade-in">
      {totalDue > 0 && (
        <div style={{ marginBottom:14, padding:'10px 14px', background:'#FEE2E2', border:'1px solid #FECACA', borderRadius:'var(--radius)', color:'#991B1B', fontSize:13, fontWeight:600 }}>
          ⚠ Outstanding dues: ₹{totalDue.toFixed(2)}
        </div>
      )}
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {bills.map(b => (
          <div key={b.id} style={{ padding:12, borderRadius:'var(--radius)', border:'1px solid #E5E7EB' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:13, fontFamily:'var(--mono)', color:'var(--teal)' }}>{b.bill_number}</div>
                <div style={{ fontSize:12, color:'#6B7280', marginTop:2 }}>{new Date(b.created_at).toLocaleString('en-IN')}</div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontWeight:700, fontSize:15 }}>₹{parseFloat(String(b.total_amount)).toFixed(2)}</div>
                <span className={`badge ${b.bill_type==='OPD'?'badge-info':b.bill_type==='IP'?'badge-warn':'badge-success'}`}>{b.bill_type}</span>
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ fontSize:11, color:'#6B7280' }}>{b.payment_mode}</span>
                <button className="btn-ghost" style={{ padding:'2px 8px', fontSize:11 }} disabled={pdfLoading===b.id} onClick={() => viewPdf(b.id)}>
                  {pdfLoading===b.id ? '…' : '📄 PDF'}
                </button>
                <PrintBillButton billId={b.id} />
              </div>
              {b.payment_status === 'Pending' ? (
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span className="badge badge-warn">Pending</span>
                  <button className="btn-ghost" style={{ padding:'2px 8px', fontSize:11 }} disabled={marking===b.id} onClick={() => markPaid(b.id)}>
                    {marking===b.id ? '…' : '✓ Mark paid'}
                  </button>
                </div>
              ) : (
                <span className="badge badge-success" style={{ fontSize:10 }}>Paid</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StageRow({ stage }: { stage: VisitStage }) {
  const meta = STAGE_META[stage.stage] || { icon:'📄', label:stage.stage };
  const d = stage.stage_data || {};
  let detail = null;

  if (stage.stage === 'vitals' && Object.keys(d).length) {
    detail = `BP ${d.bp_sys||'—'}/${d.bp_dia||'—'} · PR ${d.pulse||'—'} · T ${d.temp||'—'}°F · SpO₂ ${d.spo2||'—'}%${d.symptoms ? ` · "${d.symptoms}"` : ''}`;
  } else if (stage.stage === 'pre_consult' && Object.keys(d).length) {
    detail = `Risk: ${d.risk_level||'—'} · Pain ${d.pain_scale||'—'}/10${d.allergies ? ` · Allergies: ${d.allergies}` : ''}`;
  } else if (stage.stage === 'queue' && Object.keys(d).length) {
    detail = `Token #${d.token||'—'}${d.doctor ? ` · Dr. ${d.doctor}` : ''}${d.priority ? ` · ${d.priority}` : ''}`;
  } else if (stage.stage === 'consultation' && Object.keys(d).length) {
    detail = `${d.doc_type||'Document'} generated via ${d.provider||'AI'}${d.visit_type ? ` · Disposition: ${d.visit_type}` : ''}`;
  } else if (stage.stage === 'ip' && Object.keys(d).length) {
    detail = `Admitted · Bed ${d.bed_id||'—'}`;
  } else if (stage.stage === 'resolve' && Object.keys(d).length) {
    detail = `${d.resolution||'—'}`;
  } else if (stage.stage === 'register' && Object.keys(d).length) {
    detail = `UHID ${d.uhid||'—'}`;
  }

  return (
    <div style={{ display:'flex', gap:10, padding:'8px 0', borderBottom:'1px solid #F3F4F6' }}>
      <div style={{ width:26, textAlign:'center', flexShrink:0 }}>{meta.icon}</div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
          <span style={{ fontSize:12, fontWeight:600 }}>{meta.label}</span>
          <span style={{ fontSize:11, color:'#9CA3AF' }}>{stage.completed_at ? new Date(stage.completed_at).toLocaleString('en-IN') : ''}</span>
        </div>
        {detail && <div style={{ fontSize:12, color:'#6B7280', marginTop:2 }}>{detail}</div>}
        {stage.notes && <div style={{ fontSize:12, color:'#6B7280', marginTop:2, fontStyle:'italic' }}>"{stage.notes}"</div>}
      </div>
    </div>
  );
}

// AISummaryPanel now lives in features/patients/AISummaryPanel.tsx (shared
// with the compact strip on the Consultation screen — 2026-09-17 redesign
// Phase 3), imported above.

interface UploadProgress { name: string; pct: number }

function DocumentsTab({ uhid, consultations, onChanged }: { uhid: string; consultations: Consultation[]; onChanged?: () => void | Promise<void> }) {
  const { user } = useAuth();
  // G5: a Consultation Notes document is amended from here too (the
  // Consultation history tab sits under More, where doctors miss it).
  const [amending, setAmending] = useState<Consultation | null>(null);
  const noteFor = (doc: DocumentItem) => (doc.consultation_id ? consultations.find(c => c.id === doc.consultation_id) : undefined);
  // Gates the Release/Revoke action only — the GET/upload/delete routes above
  // stay on requireDocs server-side regardless of this flag, so hiding the
  // button here is a UX nicety, never the enforcement (the server re-checks
  // 'records:release-to-patient' on every PATCH .../release call).
  const canRelease = !!user?.modules?.includes('records:release-to-patient');
  const [cat, setCat]   = useState('Lab Report');
  const [drag, setDrag] = useState(false);
  const [up, setUp]     = useState<UploadProgress | null>(null);
  const [docs, setDocs] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [releasingId, setReleasingId] = useState<number | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [docFilter, setDocFilter] = useState<'all' | 'lab' | 'outside'>('all');
  const fref = useRef<HTMLInputElement>(null);
  // Patient → Documents → Laboratory Reports → Outside Lab Report.
  const shown = docFilter === 'outside' ? docs.filter(d => !!d.lab_context)
    : docFilter === 'lab' ? docs.filter(d => d.doc_category === 'Lab Report' || !!d.lab_context)
    : docs;

  const load = () => getPatientDocuments(uhid).then(setDocs).catch(e => setError(e instanceof Error ? e.message : String(e))).finally(() => setLoading(false));
  useEffect(() => { setLoading(true); load(); }, [uhid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    setUp({ name:file.name, pct:30 });
    try {
      await uploadPatientDocument(uhid, file, cat);
      setUp(u => (u ? { ...u, pct:100 } : u));
      await load();
      await onChanged?.();
    } catch(e) { setError(e instanceof Error ? e.message : String(e)); }
    setTimeout(() => setUp(null), 400);
  };

  const remove = async (docId: number) => {
    try { await deletePatientDocument(uhid, docId); await load(); await onChanged?.(); }
    catch(e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  // Publishes (or revokes) this document on the patient app (§17.2). Reusable
  // in both directions — the button below labels and confirms each direction
  // differently, but the call itself is the identical audited PATCH.
  const setVisibility = async (docId: number, visibility: 'staff' | 'patient') => {
    setReleasingId(docId);
    try { await releasePatientDocument(uhid, docId, visibility); await load(); }
    catch(e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setReleasingId(null); }
  };

  return (
    <div>
      <div style={{ display:'flex', gap:10, marginBottom:14, alignItems:'flex-end', flexWrap:'wrap' }}>
        <div style={{ flex:'1 1 200px', minWidth:0 }}>
          <label className="lbl">Document category</label>
          <select value={cat} onChange={e => setCat(e.target.value)}>
            {UPLOAD_CATS.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <button className="btn-primary" onClick={() => fref.current?.click()}>Upload file</button>
        <button className="btn-ghost" onClick={() => setQrOpen(true)}>📷 Scan to upload</button>
        <input ref={fref} type="file" accept=".pdf,.jpg,.jpeg,.png,.tiff" style={{ display:'none' }} onChange={e => handleFile(e.target.files?.[0])} />
      </div>

      {qrOpen && (
        <MobileUploadQRModal
          uhid={uhid} category={cat}
          onClose={() => setQrOpen(false)}
          onReceived={async () => { await load(); onChanged?.(); }}
        />
      )}

      <div style={{ border:`2px dashed ${drag?'var(--teal)':'#D1D5DB'}`, background:drag?'rgba(11,95,214,.06)':'transparent', borderRadius:'var(--radius)', padding:26, textAlign:'center', cursor:'pointer', transition:'all .2s', marginBottom:14 }}
        onDragOver={e=>{e.preventDefault();setDrag(true)}}
        onDragLeave={()=>setDrag(false)}
        onDrop={e=>{e.preventDefault();setDrag(false);handleFile(e.dataTransfer.files[0])}}
        onClick={() => fref.current?.click()}>
        <div style={{ fontSize:28, marginBottom:8 }}>📂</div>
        <div style={{ fontSize:13, fontWeight:600, color:'#374151' }}>Drop file here or click to upload</div>
        <div style={{ fontSize:12, color:'#9CA3AF', marginTop:4 }}>Supports JPG, PNG, PDF, TIFF — OCR extracts text automatically</div>
      </div>

      {error && <div style={{ marginBottom:14, padding:'8px 12px', background:'#FEE2E2', color:'#991B1B', borderRadius:'var(--radius)', fontSize:12 }}>{error}</div>}

      {up && (
        <div style={{ marginBottom:14, padding:12, background:'#F0FDF4', border:'1px solid #A7F3D0', borderRadius:'var(--radius)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:6 }}>
            <span style={{ fontWeight:600 }}>🔍 Uploading: {up.name}</span>
            <span style={{ color:'#065F46' }}>{up.pct}%</span>
          </div>
          <div style={{ height:4, background:'#E5E7EB', borderRadius:4, overflow:'hidden' }}>
            <div style={{ height:'100%', background:'var(--teal)', borderRadius:4, width:up.pct+'%', transition:'width .3s' }} />
          </div>
        </div>
      )}

      <div>
        <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
          <div className="lbl">
            {docFilter === 'all' ? `All documents (${docs.length}) — uploads and AI-generated consultation documents` : `Laboratory reports (${shown.length})`}
          </div>
          <select className="w-auto" value={docFilter} onChange={e => setDocFilter(e.target.value as 'all' | 'lab' | 'outside')}>
            <option value="all">All documents</option>
            <option value="lab">Laboratory reports</option>
            <option value="outside">Outside lab reports</option>
          </select>
        </div>
        {loading && <div style={{ textAlign:'center', padding:28, color:'#9CA3AF', fontSize:13 }}>Loading…</div>}
        {!loading && docs.length === 0 && <div style={{ textAlign:'center', padding:28, color:'#9CA3AF', fontSize:13 }}>No documents yet</div>}
        {!loading && docs.length > 0 && (
          <div style={{ overflowX:'auto' }}>
            <table>
              <thead>
                <tr>{['icon','Name','Type','Created on','Modified on','Modified by','Patient app','Link','actions'].map(h=><th key={h}>{h==='icon'||h==='actions'?'':h}</th>)}</tr>
              </thead>
              <tbody>
                {shown.map(doc => (
                  <tr key={doc.id}>
                    <td style={{ width:32 }}>
                      <div style={{ width:28, height:28, borderRadius:6, background:CCOL[doc.doc_category]||'#F3F4F6', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>
                        {CICONS[doc.doc_category]||'📄'}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight:600, fontSize:13 }}>{doc.original_name}</div>
                      {doc.source === 'consultation' && <span className="badge badge-info" style={{ fontSize:10, marginTop:2 }}>🤖 AI-generated</span>}
                      {doc.superseded_by_document_id && <span className="badge ml-1 mt-0.5 text-[10px]">Superseded by an amendment</span>}
                      {doc.source === 'mobile_upload' && <span className="badge badge-info" style={{ fontSize:10, marginTop:2 }}>📱 From mobile</span>}
                      {doc.lab_context && (
                        <div className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                          <div>
                            <Badge variant="info">🚚 Outside lab report</Badge>{' '}
                            {doc.lab_context.reference_lab ?? 'Reference lab'}
                            {doc.lab_context.report_date ? ` · report dated ${new Date(doc.lab_context.report_date).toLocaleDateString('en-IN')}` : ''}
                          </div>
                          {doc.lab_context.tests.map((t) => {
                            const st = outsideLabStatus(t);
                            return (
                              <div key={t.order_item_id}>
                                Test: <span className="font-semibold text-foreground">{t.test_name}</span> · {t.order_number}{' '}
                                <Badge variant={st.variant}>{st.label}</Badge>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td><span className="badge badge-gray" style={{ background:'#F3F4F6', color:'#4B5563' }}>{doc.doc_category}</span></td>
                    <td style={{ fontSize:12, color:'#6B7280' }}>{new Date(doc.uploaded_at).toLocaleString('en-IN')}</td>
                    <td style={{ fontSize:12, color:'#6B7280' }}>{doc.modified_at ? new Date(doc.modified_at).toLocaleString('en-IN') : '—'}</td>
                    <td style={{ fontSize:12, color:'#6B7280' }}>{doc.modified_by || '—'}</td>
                    <td>
                      {doc.visibility === 'patient' ? (
                        <Badge variant="success">✓ Released</Badge>
                      ) : (
                        <Badge variant="neutral">Staff only</Badge>
                      )}
                      {canRelease && (
                        <button
                          className="btn-ghost"
                          style={{ display:'block', marginTop:4, padding:'2px 8px', fontSize:11 }}
                          disabled={releasingId === doc.id}
                          onClick={() => setVisibility(doc.id, doc.visibility === 'patient' ? 'staff' : 'patient')}
                        >
                          {releasingId === doc.id ? '…' : doc.visibility === 'patient' ? 'Revoke' : 'Release to patient'}
                        </button>
                      )}
                    </td>
                    <td>
                      <DocumentPreviewButtons uhid={uhid} docId={doc.id} label={doc.original_name} className="flex gap-1" />
                      {(() => {
                        const note = noteFor(doc);
                        return note && !doc.superseded_by_document_id && canAmendNote(note, user) ? (
                          <button type="button" className="btn-ghost mt-1" style={{ padding:'5px 12px', fontSize:12 }} onClick={() => setAmending(note)}>
                            ✎ Amend note
                          </button>
                        ) : null;
                      })()}
                    </td>
                    <td>
                      <button className="btn-danger" style={{ padding:'4px 10px', fontSize:12 }} onClick={() => remove(doc.id)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <AmendNoteDialog
        note={amending}
        onClose={() => setAmending(null)}
        onAmended={async (a) => {
          setAmending(null);
          toast({ title: `Amendment saved as version ${a.version ?? ''}`, variant: 'success' });
          await load();
          await onChanged?.();
        }}
      />
    </div>
  );
}

function HistoryTab({ consultations, onChanged }: { consultations: Consultation[]; onChanged: () => Promise<void> }) {
  const ICONS: Record<string, string> = { 'OPD Prescription':'💊', 'Consultation Notes':'📝', 'Discharge Summary':'📋' };
  const TCOL: Record<string, string>  = { 'OPD Prescription':'#D1FAE5', 'Consultation Notes':'#DBEAFE', 'Discharge Summary':'#FEF3C7' };
  const { user } = useAuth();
  const [amending, setAmending] = useState<Consultation | null>(null);
  const [historyOf, setHistoryOf] = useState<number | null>(null);
  // G5: an amended note's earlier versions stay in the record but are shown
  // through "Version history", not as separate entries in the timeline.
  // An amendment keeps its place in the timeline: order by the first
  // version's date, not by when the amendment was saved.
  const byId = new Map(consultations.map(c => [c.id, c]));
  const firstDate = (c: Consultation): number => {
    let root = c;
    for (let n = 0; root.amends_consultation_id && byId.has(root.amends_consultation_id) && n < 50; n++) root = byId.get(root.amends_consultation_id)!;
    return new Date(root.approved_at || (root as { created_at?: string }).created_at || 0).getTime();
  };
  const current = consultations.filter(c => !c.superseded_by_consultation_id)
    .sort((a, b) => firstDate(b) - firstDate(a));
  if (current.length === 0) return <div style={{ textAlign:'center', padding:36, color:'#6B7280' }}>No consultation history recorded</div>;
  return (
    <div className="fade-in">
      {[...current].reverse().map((c, i) => (
        <div key={c.id} style={{ display:'flex', gap:14, paddingBottom:18, position:'relative' }}>
          {i < current.length - 1 && <div style={{ position:'absolute', left:14, top:32, bottom:0, width:1, background:'#E5E7EB' }} />}
          <div style={{ width:30, height:30, borderRadius:'50%', background:TCOL[c.doc_type]||'#F3F4F6', border:`2px solid ${TCOL[c.doc_type]||'#E5E7EB'}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, flexShrink:0 }}>
            {ICONS[c.doc_type]||'📄'}
          </div>
          <div style={{ flex:1, paddingTop:2 }}>
            <div className="mb-[7px] flex flex-wrap items-center justify-between gap-2">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className={`badge ${c.doc_type==='OPD Prescription'?'badge-success':c.doc_type==='Consultation Notes'?'badge-info':'badge-warn'}`}>{c.doc_type}</span>
                <VersionTag note={c} />
              </span>
              <span className="flex items-center gap-2 text-[11px] text-ink-muted">
                {c.approved_at ? new Date(c.approved_at).toLocaleString('en-IN') : (c.approved ? '' : 'Not yet approved')}
                {(c.version ?? 1) > 1 && (
                  <button type="button" className="text-teal underline-offset-2 hover:underline" onClick={() => setHistoryOf(c.id)}>Version history</button>
                )}
                {canAmendNote(c, user) && (
                  <button type="button" className="btn-ghost" style={{ padding:'3px 10px', fontSize:11.5 }} onClick={() => setAmending(c)}>✎ Amend</button>
                )}
              </span>
            </div>
            {c.amendment_reason && <div className="mb-1 text-[11.5px] text-ink-muted">Amended: {c.amendment_reason}</div>}
            <div style={{ background:'#F9FAFB', border:'1px solid #E5E7EB', borderRadius:'var(--radius)', padding:'10px 12px', fontSize:12, fontFamily:'monospace', lineHeight:1.7, color:'#374151', whiteSpace:'pre-wrap', maxHeight:130, overflow:'auto' }}>
              {c.final_doc || c.ai_draft}
            </div>
          </div>
        </div>
      ))}
      <AmendNoteDialog
        note={amending}
        onClose={() => setAmending(null)}
        onAmended={async (a) => {
          setAmending(null);
          toast({ title: `Amendment saved as version ${a.version ?? ''}`, variant: 'success' });
          await onChanged();
        }}
      />
      <NoteVersionsDialog noteId={historyOf} onClose={() => setHistoryOf(null)} />
    </div>
  );
}

interface EditForm {
  title: string;
  name: string;
  mobile: string;
  age: number | string;
  dob: string;
  address: string;
  abha_number: string;
  blood_group: string;
  height_cm: string;
}

function EditModal({ patient, onClose, onSaved }: { patient: Patient; onClose: () => void; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState<EditForm>({
    title: patient.title || '',
    name: patient.name, mobile: patient.mobile || '', age: patient.age||'',
    dob: toDateInputValue(patient.dob),
    address: patient.address||'', abha_number: patient.abha_number||'',
    blood_group: patient.blood_group||'', height_cm: patient.height_cm!=null ? String(patient.height_cm) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const f = (k: keyof EditForm, v: string) => setForm(p => ({ ...p, [k]: v }));

  // Title dropdown — shown when the hospital asks for it, or when this
  // patient already has one (so it can still be corrected or cleared after
  // an admin hides the field or removes that option).
  const { data: titleCfg } = useTitleField();
  const showTitle = !!patient.title || (!!titleCfg && titleCfg.status !== 'hidden');
  const titleChoices = (() => {
    const list = titleCfg?.options ?? [];
    return patient.title && !list.includes(patient.title) ? [patient.title, ...list] : list;
  })();

  const save = async () => {
    if (!form.name.trim() || !form.mobile.trim()) { setError('Name and mobile are required'); return; }
    setSaving(true); setError('');
    try {
      const { title, ...rest } = form;
      await updatePatient(patient.uhid, {
        ...rest,
        // Capitalized on save only when staff changed the name, so fixing a
        // mobile number never silently rewrites an older record's name.
        name: form.name !== patient.name ? capitalizeNameParts(form.name) : form.name,
        // Sent only when the dropdown is on screen; '' clears the title.
        ...(showTitle ? { title } : {}),
        age: form.age === '' ? null : form.age,
        dob: form.dob || null,
        address: form.address || null,
        abha_number: form.abha_number || null,
        blood_group: form.blood_group || null,
        height_cm: form.height_cm === '' ? null : form.height_cm,
      });
      await onSaved();
    } catch(e) { setError(e instanceof Error ? e.message : String(e)); setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
      <div className="card" style={{ width:'min(480px, calc(100vw - 24px))', padding:20, maxHeight:'85vh', overflow:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
          <div style={{ fontWeight:600, fontSize:15 }}>Edit patient record</div>
          <button className="btn-ghost" style={{ padding:'4px 10px' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize:13, color:'#6B7280', marginBottom:16, padding:'8px 12px', background:'#FFFBEB', borderRadius:'var(--radius)', border:'1px solid #FDE68A' }}>
          ⚠ Editing patient records updates the master record. Changes are audit-logged.
        </div>
        {error && <div style={{ marginBottom:12, color:'#DC2626', fontSize:13 }}>{error}</div>}
        <div className="grid2" style={{ marginBottom:14 }}>
          {showTitle && (
            <div>
              <label className="lbl">Title</label>
              <select aria-label="Title" value={form.title} onChange={e=>f('title',e.target.value)}>
                <option value="">— No title —</option>
                {titleChoices.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          {([['Patient name','name'],['Mobile','mobile'],['Age','age'],['Address','address'],['ABHA number','abha_number']] as [string, keyof EditForm][]).map(([l,k]) => (
            <div key={k} style={{ gridColumn:(k==='address')?'1/-1':'auto' }}>
              <label className="lbl">{l}</label>
              {k === 'name'
                ? <input value={form.name} {...nameInputProps(v => f('name', v))} />
                : <input value={form[k]} onChange={e=>f(k,e.target.value)} />}
            </div>
          ))}
          <div>
            <label className="lbl">Date of birth</label>
            <input type="date" value={form.dob} onChange={e=>f('dob',e.target.value)} />
          </div>
          <div>
            <label className="lbl">Blood group</label>
            <select value={form.blood_group} onChange={e=>f('blood_group',e.target.value)}>
              {['','A+','A-','B+','B-','O+','O-','AB+','AB-'].map(b=><option key={b} value={b}>{b||'Unknown'}</option>)}
            </select>
          </div>
          <div>
            <label className="lbl">Height (cm)</label>
            <input type="number" value={form.height_cm} onChange={e=>f('height_cm',e.target.value)} />
          </div>
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>
    </div>
  );
}
